import React, { useState, useEffect } from "react";
import { createBrowserRouter, Navigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./components/admin/AdminLayout";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmailAction from "./pages/VerifyEmailAction";
import Farm from "./pages/Farm";
import Messages from "./pages/Messages";
import ConversationsList from "./pages/ConversationsList";
import Notifications from "./pages/Notifications";
import Inbox from "./pages/Inbox";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Settings from "./pages/Settings";
import ProductDetails from "./pages/ProductDetails";
import MyProducts from "./pages/MyProducts";
import Wishlist from "./pages/Wishlist";
import NorthStar from "./pages/NorthStar";
import FarmMark from "./pages/FarmMark";
import MyImpact from "./pages/MyImpact";
import SearchResults from "./pages/SearchResults";
import EditProduct from "./pages/EditProduct";
import OrdersOffers from "./pages/OrdersOffers";
import FarmPulse from "./pages/FarmPulse";
import SellNow from "./pages/SellNow";
import MeProfileRedirect from "./pages/MeProfileRedirect";
import MoreNew from "./pages/MoreNew";
import OurPlatform from "./pages/OurPlatform";

import OrrithLanding from "./pages/OrrithLanding";
import NotFound from "./pages/NotFound";

import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import Security from "./pages/legal/Security";
import CommunityGuidelines from "./pages/legal/CommunityGuidelines";
import CookiePolicy from "./pages/legal/CookiePolicy";
import DataProcessingAddendum from "./pages/legal/DataProcessingAddendum";
import RefundPolicy from "./pages/legal/RefundPolicy";
import Accessibility from "./pages/Accessibility";
import Careers from "./pages/Careers";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminModeration from "./pages/admin/AdminModeration";
import AdminReports from "./pages/admin/AdminReports";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminActivityLog from "./pages/admin/AdminActivityLog";
import AdminSupportChat from "./pages/admin/AdminSupportChat";
import AdminPopupManager from "./pages/admin/AdminPopupManager";

import Reviews from "./pages/view-profile/Reviews";

import SupportCenter from "./pages/SupportCenter";
import MyTickets from "./pages/support/MyTickets";
import TicketDetail from "./pages/support/TicketDetail";
import HelpArticle from "./pages/HelpArticle";
import LoadingSpinner from "./components/ui/LoadingSpinner";

function Private({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading..." tone="primary" />
      </div>
    );
  }

  return user ? (
    children
  ) : (
    <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  );
}

function useIsMobile() {
  const detectMobile = () => {
    if (typeof window === "undefined") return false;

    const isSmallViewport = window.innerWidth < 768;
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return isSmallViewport || isMobileUserAgent;
  };

  const [isMobile, setIsMobile] = useState(detectMobile);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(detectMobile());
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  return isMobile;
}

function RootRedirect() {
  return <Navigate to="/farm" replace />;
}

function createAppRoutes({ layoutElement, darkMode, setDarkMode, user, mobile, setMobile, isMobile }) {
  const getDefaultRoute = () => "/farm";

  return [
    {
      path: "/admin",
      element: (
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      ),
      children: [
        { index: true, element: <AdminDashboard /> },
        { path: "notifications", element: <AdminNotifications /> },
        { path: "users", element: <AdminUsers /> },
        { path: "tickets", element: <AdminTickets /> },
        { path: "support-chat", element: <AdminSupportChat /> },
        { path: "feedback", element: <AdminFeedback /> },
        { path: "moderation", element: <AdminModeration /> },
        { path: "reports", element: <AdminReports /> },
        { path: "analytics", element: <AdminAnalytics /> },
        { path: "activity", element: <AdminActivityLog /> },
        { path: "activity-log", element: <Navigate to="/admin/activity" replace /> },
        { path: "popups", element: <AdminPopupManager /> },
      ],
    },
    {
      element: layoutElement || <Outlet />,
      errorElement: <Navigate to={getDefaultRoute()} replace />,
      children: [
        { path: "/", element: isMobile ? <Navigate to="/farm" replace /> : <OrrithLanding /> },

        { path: "/login", element: <Login onSuccess={() => (window.location.href = "/north-star")} /> },
        { path: "/signup", element: <Signup onSuccess={() => (window.location.href = "/north-star")} /> },
        { path: "/forgot-password", element: <ForgotPassword /> },
        { path: "/verify-email-action", element: <VerifyEmailAction /> },
        { path: "/farm", element: <Farm /> },
        { path: "/farmmark", element: <FarmMark /> },
        { path: "/farmpulse", element: <FarmPulse /> },
        { path: "/orrith-landing", element: <Navigate to="/" replace /> },
        { path: "/our-platform", element: <OurPlatform /> },
        { path: "/gaia-core-landing", element: <Navigate to="/orrith-landing" replace /> },
        { path: "/search", element: <SearchResults /> },
        { path: "/north-star", element: <NorthStar /> },

        { path: "/legal/privacy", element: <PrivacyPolicy /> },
        { path: "/legal/terms", element: <TermsOfService /> },
        { path: "/legal/security", element: <Security /> },
        { path: "/legal/community-guidelines", element: <CommunityGuidelines /> },
        { path: "/legal/cookies", element: <CookiePolicy /> },
        { path: "/legal/dpa", element: <DataProcessingAddendum /> },
        { path: "/legal/refund", element: <RefundPolicy /> },
        { path: "/accessibility", element: <Accessibility /> },
        { path: "/careers", element: <Careers /> },

        { path: "/profile/:userId", element: <Private><Profile /></Private> },
        { path: "/product/:id", element: <ProductDetails /> },
        { path: "/wishlist", element: <Private><Wishlist /></Private> },

        { path: "/sellnow", element: <ProtectedRoute requireEmailVerified={true}><SellNow /></ProtectedRoute> },
        { path: "/inbox", element: <ProtectedRoute requireEmailVerified={true}><Inbox /></ProtectedRoute> },
        { path: "/notifications", element: <ProtectedRoute requireEmailVerified={true}><Notifications /></ProtectedRoute> },
        { path: "/messages", element: <ProtectedRoute requireEmailVerified={true}><Messages /></ProtectedRoute> },
        { path: "/messages/:chatId", element: <ProtectedRoute requireEmailVerified={true}><Messages /></ProtectedRoute> },
        { path: "/conversations", element: <ProtectedRoute requireEmailVerified={true}><ConversationsList /></ProtectedRoute> },
        { path: "/edit-profile", element: <ProtectedRoute requireEmailVerified={false}><EditProfile /></ProtectedRoute> },
        { path: "/edit-product/:id", element: <ProtectedRoute requireEmailVerified={true}><EditProduct /></ProtectedRoute> },
        { path: "/my-offers", element: <ProtectedRoute requireEmailVerified={true}><OrdersOffers /></ProtectedRoute> },
        { path: "/offers", element: <Navigate to="/my-offers" replace /> },
        { path: "/my-impact", element: <MyImpact /> },
        { path: "/me", element: <ProtectedRoute requireEmailVerified={false}><MeProfileRedirect /></ProtectedRoute> },
        { path: "/more", element: <ProtectedRoute requireEmailVerified={false}><MoreNew /></ProtectedRoute> },
        { path: "/my-products", element: <ProtectedRoute requireEmailVerified={true}><MyProducts /></ProtectedRoute> },
        { path: "/settings", element: <ProtectedRoute requireEmailVerified={false}><Settings darkMode={darkMode} setDarkMode={setDarkMode} user={user} /></ProtectedRoute> },
        { path: "/profile/:userId/reviews", element: <Private><Reviews /></Private> },

        { path: "/support", element: <SupportCenter /> },
        { path: "/support/tickets", element: <MyTickets /> },
        { path: "/support/tickets/:id", element: <TicketDetail /> },
        { path: "/help/:id", element: <HelpArticle /> },

        { path: "/privacy", element: <Navigate to="/legal/privacy" replace /> },
        { path: "/terms", element: <Navigate to="/legal/terms" replace /> },

        // 404 page for genuinely unknown routes
        { path: "/404", element: <NotFound /> },

        { path: "*", element: <NotFound /> },
      ],
    },
  ];
}

function createAppRouter(config) {
  const routes = createAppRoutes(config);

  return createBrowserRouter(routes, {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  });
}

export { createAppRouter, useIsMobile, Private, RootRedirect };
